import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    OffroadTrailType,
    PlannedRideContract,
    RouteRequestSegmentContract,
    RoutingRequestContract,
    RoutingResponseContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { RoutingHelpersService } from '@polaris-customerweb/sharedcomponents.shared';
import { TrackedRide } from '@polaris-customerweb/sharedcomponents.user-content';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MapboxDirectionsResponseDto } from 'src/app/dtos/mapbox-directions-response.dto';
import { AppState } from '../models/app-state';
import { selectSnapToTrackedRideEnabled } from '../state/map/layer-visibility/layer-visibility.reducer';

@Injectable({ providedIn: 'root' })
export class PlannedRideResource {
    includeNonDesignated: boolean;
    snapToTrackedEnabled = false;
    showingTrackedRides = false;
    trackedRides: TrackedRide[];

    private geoFeaturesUrl: string;
    private plannedRideUrl: string;

    constructor(
        private configService: ConfigService,
        private http: HttpClient,
        private routingHelpersService: RoutingHelpersService,
        private store: Store<AppState>
    ) {
        this.geoFeaturesUrl = this.routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'geofeatures/planned-ride'
        );
        this.plannedRideUrl = this.routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'v2/planned-rides'
        );
        this.store
            .select(selectSnapToTrackedRideEnabled)
            .subscribe((enabled) => {
                this.snapToTrackedEnabled = enabled;
            });
    }

    getRoute(
        mapboxWaypoints: string[],
        excludeRouteOptions?: string
    ): Observable<MapboxDirectionsResponseDto> {
        const query = mapboxWaypoints.join(';');
        const url = this.routingHelpersService.combineUrl(
            'https://api.mapbox.com/directions/v5/mapbox/driving',
            query
        );
        let params = new HttpParams()
            .set('geometries', 'geojson')
            .set('overview', 'full')
            .set('access_token', this.configService.mapboxToken);

        if (excludeRouteOptions) {
            params = params.set('exclude', excludeRouteOptions);
        }

        return this.http.get<MapboxDirectionsResponseDto>(url, { params });
    }

    getTrailRoute(
        segments: RouteRequestSegmentContract[],
        trails: OffroadTrailType[],
        trackedRides: TrackedRide[]
    ): Observable<RoutingResponseContract> {
        const url = this.routingHelpersService.combineUrl(
            this.configService.apiUrl.routing,
            'api/v1/Routing/route'
        );
        const data: RoutingRequestContract = {
            acceptableDistanceOffTrail: undefined,
            segments,
            trailTypes: trails,
            userTracks: trackedRides.map((x) =>
                x.toRoutingRequestUserTrackContract()
            ),
        };

        return this.http.post<RoutingResponseContract>(url, data);
    }

    getPlannedRides(): Observable<PlannedRideContract[]> {
        const url = `${this.plannedRideUrl}?includeDeleted=false`;
        return this.http.get<PlannedRideContract[]>(url);
    }

    getPlannedRideById(id: string): Observable<PlannedRideContract> {
        const url = this.routingHelpersService.combineUrl(
            this.plannedRideUrl,
            id
        );

        return this.http.get<PlannedRideContract>(url);
    }

    createPlannedRide(
        plannedRideContract: PlannedRideContract
    ): Observable<{ id: string }> {
        return this.http.post<{ id: string }>(
            this.plannedRideUrl,
            plannedRideContract
        );
    }

    updatePlannedRide(
        plannedRideContract: PlannedRideContract
    ): Observable<void> {
        const url = this.routingHelpersService.combineUrl(
            this.plannedRideUrl,
            plannedRideContract?.id || ''
        );

        return this.http.put<undefined>(url, plannedRideContract);
    }

    deletePlannedRide(id: string): Observable<void> {
        const url = this.routingHelpersService.combineUrl(
            this.plannedRideUrl,
            id
        );

        return this.http.delete<undefined>(url);
    }

    convertGpxToPlannedRide(
        formData: FormData
    ): Observable<PlannedRideContract> {
        const url = this.routingHelpersService.combineUrl(
            this.geoFeaturesUrl,
            'import-gpx'
        );

        return this.http.post<PlannedRideContract>(url, formData, {
            responseType: 'json',
        });
    }

    updateFlyByUrl(id: string): Observable<string> {
        const url = this.routingHelpersService.combineUrl(
            this.geoFeaturesUrl,
            id,
            'flyby'
        );

        return this.http
            .post<{ flyByUrl: string }>(url, {})
            .pipe(map((result) => result.flyByUrl));
    }
}
